import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronDown, CircleHelp, Plus } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Skeleton } from '@/components/ui/skeleton';

import { PeriodControl } from '../../../components/design-system/analytics/PeriodControl';
import { PageContainer } from '../../../common/components/PageContainer';
import { ShopperQuestionTheme, ShopperQuestions, useShopperInsightsApi } from '../../../common/hooks/use-shopper-insights-api.hook';
import { FeatureGate } from '../../../components/FeatureGate';
import { EmptyState } from '../../../components/design-system/admin/EmptyState';
import { SectionCard } from '../../../components/design-system/admin/SectionCard';
import { StatusBadge } from '../../../components/design-system/admin/StatusBadge';
import { formatNumber } from '../../../components/design-system/admin/format';
import { BarList } from '../../../components/design-system/analytics/BarList';
import { TaskItem, TaskList } from '../../../components/design-system/analytics/TaskList';
import { useInsightsRange } from './assistant-shared';
import { describeSearch, groupUnanswered, themeAnsweredWell, themeRows } from './assistant-reading';
import { AssistantHeader, useAskArdiAbout, useReasonLabel, useThemeLabel } from './assistant-ui';

/** One theme: what it is, how many asked, whether they got answers — and, opened, the questions themselves. */
const ThemeRow = ({ theme, max, open, onOpenChange }: {
    theme: ShopperQuestionTheme;
    max: number;
    open: boolean;
    onOpenChange: (open: boolean) => void;
}) => {
    const { t } = useTranslation();
    const themeLabel = useThemeLabel();
    const ref = useRef<HTMLLIElement>(null);
    const well = themeAnsweredWell(theme);

    // Opened from a link (?theme=delivery): bring it into view.
    useEffect(() => {
        if (open) ref.current?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <li ref={ref} id={`theme-${theme.theme}`}>
            <Collapsible open={open} onOpenChange={onOpenChange}>
                <CollapsibleTrigger className="group/theme flex w-full items-center gap-4 px-6 py-3.5 text-start transition-colors hover:bg-muted/40">
                    <span className="min-w-0 flex-1">
                        <span className="flex items-baseline justify-between gap-3">
                            <span className="truncate text-sm font-medium">{themeLabel(theme.theme)}</span>
                            <span className="shrink-0 text-sm tabular-nums">{formatNumber(theme.count)}</span>
                        </span>
                        <span className="mt-2 block h-1.5 w-full overflow-hidden rounded-full bg-muted" aria-hidden>
                            <span className="block h-full rounded-full bg-foreground/60" style={{ width: `${Math.max(2, (theme.count / Math.max(1, max)) * 100)}%` }} />
                        </span>
                    </span>
                    <span className="hidden w-40 shrink-0 sm:block">
                        {well ? (
                            <StatusBadge tone="success">{t('SHOPPER_UI.QUESTIONS.ANSWERED_WELL')}</StatusBadge>
                        ) : (
                            <StatusBadge tone="warning">{t('SHOPPER_UI.COMMON.NOT_ANSWERED_COUNT', { count: theme.unanswered })}</StatusBadge>
                        )}
                    </span>
                    <ChevronDown className="size-4 shrink-0 text-muted-foreground transition-transform group-data-[state=open]/theme:rotate-180" />
                </CollapsibleTrigger>
                <CollapsibleContent>
                    <div className="px-6 pb-4">
                        <span className="mb-2 block sm:hidden">
                            {well
                                ? <StatusBadge tone="success">{t('SHOPPER_UI.QUESTIONS.ANSWERED_WELL')}</StatusBadge>
                                : <StatusBadge tone="warning">{t('SHOPPER_UI.COMMON.NOT_ANSWERED_COUNT', { count: theme.unanswered })}</StatusBadge>}
                        </span>
                        <p className="mb-2 text-xs text-muted-foreground">{t('SHOPPER_UI.QUESTIONS.EXAMPLES')}</p>
                        <ul className="flex flex-col gap-1.5">
                            {theme.examples.map((example) => (
                                <li key={example.question} className="flex items-start justify-between gap-3 rounded-lg bg-muted/50 px-3 py-2 text-sm">
                                    <span className="min-w-0">
                                        “{example.question}”
                                        {example.unanswered > 0 && (
                                            <StatusBadge tone="warning" className="ms-2 align-middle text-muted-foreground">
                                                {t('SHOPPER_UI.COMMON.NOT_ANSWERED_COUNT', { count: example.unanswered })}
                                            </StatusBadge>
                                        )}
                                    </span>
                                    <span className="shrink-0 text-xs tabular-nums text-muted-foreground">×{formatNumber(example.count)}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </CollapsibleContent>
            </Collapsible>
        </li>
    );
};

/**
 * What shoppers ask, grouped the way a vendor thinks about it.
 *
 * First the questions the assistant could not answer, each with the way to
 * fix it; then the themes, each saying whether shoppers are getting answers;
 * then the products shoppers looked for and did not find, and the ones the
 * assistant recommends most. The raw search filters are folded away.
 */
const QuestionsBody = () => {
    const { t } = useTranslation();
    const api = useShopperInsightsApi();
    const { range, periodProps } = useInsightsRange();
    const [params, setParams] = useSearchParams();
    const reasonLabel = useReasonLabel();
    const askArdi = useAskArdiAbout();

    const [data, setData] = useState<ShopperQuestions | null>(null);
    const [loading, setLoading] = useState(true);
    const [showAll, setShowAll] = useState(false);
    const openTheme = params.get('theme');

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        api.getQuestions(range)
            .then((r) => { if (!cancelled) setData(r); })
            .catch((error) => { console.error('Error loading shopper questions:', error); if (!cancelled) setData(null); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [range.startDate, range.endDate]);

    const setOpenTheme = (theme: string | null) => {
        const next = new URLSearchParams(params);
        if (theme) next.set('theme', theme); else next.delete('theme');
        setParams(next, { replace: true });
    };

    const needs = useMemo(() => groupUnanswered(data?.unanswered, 50), [data]);
    const themes = themeRows(data);
    const maxTheme = Math.max(1, ...themes.map((x) => x.count));
    const none = t('SHOPPER_UI.COMMON.NO_DATA');
    const nothingAsked = !loading && !!data && !data.topQuestions?.length && !needs.length;

    const tasks: TaskItem[] = needs.map((item) => ({
        id: item.key,
        tone: 'watch',
        title: item.question ? `“${item.question}”` : t('SHOPPER_UI.COMMON.NO_QUESTION'),
        detail: [reasonLabel(item.reason), item.sku ? t('SHOPPER_UI.CONVERSATIONS.ON_PRODUCT', { sku: item.sku }) : null].filter(Boolean).join(' · '),
        meta: item.count > 1 ? t('SHOPPER_UI.COMMON.ASKED_TIMES', { count: item.count }) : undefined,
        href: `/assistant/conversations?open=${item.threadUuid}`,
        action: askArdi && item.question && item.reason === 'no_match'
            ? { label: t('SHOPPER_UI.ASK_ARDI.LABEL'), onClick: () => askArdi(item.question, item.sku) }
            : undefined,
    }));

    const disliked: TaskItem[] = (data?.disliked || []).map((entry, i) => ({
        id: `${entry.threadUuid}-${i}`,
        tone: 'bad',
        title: entry.question ? `“${entry.question}”` : t('SHOPPER_UI.COMMON.NO_QUESTION'),
        detail: entry.answer || undefined,
        href: `/assistant/conversations?open=${entry.threadUuid}`,
    }));

    const notFound = (data?.emptySearches || [])
        .map((s) => ({ key: s.filters, label: describeSearch(s.filters), value: s.count }))
        .filter((s) => s.label);

    const filterLabels: Record<string, string> = {
        colour: t('SHOPPING_ASSISTANT.QUESTIONS.FILTER_COLOUR'),
        material: t('SHOPPING_ASSISTANT.QUESTIONS.FILTER_MATERIAL'),
        style: t('SHOPPING_ASSISTANT.QUESTIONS.FILTER_STYLE'),
        room: t('SHOPPING_ASSISTANT.QUESTIONS.FILTER_ROOM'),
        surface: t('SHOPPING_ASSISTANT.QUESTIONS.FILTER_SURFACE'),
        finish: t('SHOPPING_ASSISTANT.QUESTIONS.FILTER_FINISH'),
        colourTemperature: t('SHOPPING_ASSISTANT.QUESTIONS.FILTER_TEMPERATURE'),
        query: t('SHOPPING_ASSISTANT.QUESTIONS.FILTER_QUERY'),
    };

    return (
        <div className="mx-auto flex w-full max-w-[1100px] flex-col gap-6">
            <AssistantHeader
                title={t('SHOPPER_UI.QUESTIONS.TITLE')}
                subtitle={t('SHOPPER_UI.QUESTIONS.SUBTITLE')}
                actions={<PeriodControl {...periodProps} />}
            />

            {data?.partial && <p className="text-xs text-muted-foreground">{t('SHOPPER_UI.COMMON.PARTIAL')}</p>}

            {nothingAsked ? (
                <EmptyState
                    icon={<CircleHelp />}
                    title={t('SHOPPER_UI.QUESTIONS.EMPTY_TITLE')}
                    description={t('SHOPPER_UI.QUESTIONS.EMPTY_TEXT')}
                />
            ) : (
                <>
                    <TaskList
                        title={t('SHOPPER_UI.NEEDS.TITLE')}
                        description={t('SHOPPER_UI.QUESTIONS.NEEDS_DESC')}
                        items={tasks}
                        loading={loading}
                        max={showAll ? tasks.length : 8}
                        emptyTitle={t('SHOPPER_UI.NEEDS.EMPTY_TITLE')}
                        emptyText={t('SHOPPER_UI.NEEDS.EMPTY_TEXT')}
                        more={showAll ? undefined : { label: (hidden) => t('SHOPPER_UI.COMMON.SHOW_MORE', { count: hidden }), onClick: () => setShowAll(true) }}
                        testId="questions-needs-you"
                    />

                    <SectionCard
                        title={t('SHOPPER_UI.QUESTIONS.THEMES_TITLE')}
                        description={t('SHOPPER_UI.QUESTIONS.THEMES_DESC')}
                        flush
                    >
                        {loading ? (
                            <div className="flex flex-col gap-3 px-6 pb-6"><Skeleton className="h-10 w-full" /><Skeleton className="h-10 w-full" /></div>
                        ) : themes.length ? (
                            <ul className="divide-y border-t" data-testid="question-themes">
                                {themes.map((theme) => (
                                    <ThemeRow
                                        key={theme.theme}
                                        theme={theme}
                                        max={maxTheme}
                                        open={openTheme === theme.theme}
                                        onOpenChange={(value) => setOpenTheme(value ? theme.theme : null)}
                                    />
                                ))}
                            </ul>
                        ) : (
                            // An API from before themes: the most asked wordings, as they were.
                            <div className="px-6 pb-6">
                                <BarList
                                    emptyText={none}
                                    items={(data?.topQuestions || []).slice(0, 10).map((q) => ({ key: q.question, label: q.question, value: q.count }))}
                                />
                            </div>
                        )}
                    </SectionCard>

                    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                        <SectionCard
                            title={t('SHOPPER_UI.QUESTIONS.NOT_FOUND_TITLE')}
                            description={t('SHOPPER_UI.QUESTIONS.NOT_FOUND_DESC')}
                            action={(
                                <Button variant="outline" size="sm" asChild>
                                    <Link to="/products/new"><Plus />{t('SHOPPER_UI.QUESTIONS.ADD_PRODUCT')}</Link>
                                </Button>
                            )}
                        >
                            {loading ? <Skeleton className="h-24 w-full" /> : <BarList emptyText={t('SHOPPER_UI.QUESTIONS.NOT_FOUND_EMPTY')} items={notFound.slice(0, 8)} />}
                        </SectionCard>
                        <SectionCard title={t('SHOPPER_UI.QUESTIONS.RECOMMENDED_TITLE')} description={t('SHOPPER_UI.QUESTIONS.RECOMMENDED_DESC')}>
                            {loading ? <Skeleton className="h-24 w-full" /> : (
                                <BarList
                                    emptyText={none}
                                    items={(data?.topRecommended || []).slice(0, 8).map((p) => ({ key: p.sku, label: p.name || p.sku, value: p.count, secondary: p.name ? p.sku : undefined }))}
                                />
                            )}
                        </SectionCard>
                    </div>

                    {disliked.length > 0 && (
                        <TaskList
                            title={t('SHOPPER_UI.QUESTIONS.DISLIKED_TITLE')}
                            description={t('SHOPPER_UI.QUESTIONS.DISLIKED_DESC')}
                            items={disliked}
                            max={5}
                            emptyTitle=""
                        />
                    )}

                    {!loading && data && (Object.keys(data.searchFilters || {}).length > 0 || (data.topProducts || []).length > 0) && (
                        <Collapsible className="rounded-xl border bg-card">
                            <CollapsibleTrigger className="group/more flex w-full items-center justify-between gap-3 px-6 py-4 text-start">
                                <span>
                                    <span className="block text-sm font-medium">{t('SHOPPER_UI.QUESTIONS.MORE_TITLE')}</span>
                                    <span className="mt-0.5 block text-xs text-muted-foreground">{t('SHOPPER_UI.QUESTIONS.MORE_DESC')}</span>
                                </span>
                                <ChevronDown className="size-4 shrink-0 text-muted-foreground transition-transform group-data-[state=open]/more:rotate-180" />
                            </CollapsibleTrigger>
                            <CollapsibleContent className="grid grid-cols-1 gap-6 border-t px-6 py-5 md:grid-cols-2">
                                <div className="flex flex-col gap-4">
                                    <p className="text-xs font-medium text-muted-foreground">{t('SHOPPER_UI.QUESTIONS.LOOKED_FOR')}</p>
                                    {Object.entries(data.searchFilters || {}).map(([field, values]) => (
                                        <div key={field}>
                                            <p className="mb-1.5 text-xs text-muted-foreground">{filterLabels[field] || field}</p>
                                            <div className="flex flex-wrap gap-1.5">
                                                {values.map((v) => (
                                                    <span key={v.value} className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs">
                                                        {v.value}<span className="tabular-nums text-muted-foreground">{v.count}</span>
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                <div>
                                    <p className="mb-2 text-xs font-medium text-muted-foreground">{t('SHOPPER_UI.QUESTIONS.ASKED_ABOUT')}</p>
                                    <BarList emptyText={none} items={(data.topProducts || []).slice(0, 10).map((p) => ({ key: p.sku, label: p.sku, value: p.count }))} />
                                </div>
                            </CollapsibleContent>
                        </Collapsible>
                    )}
                </>
            )}
        </div>
    );
};

export const AssistantQuestionsPage = () => {
    const { t } = useTranslation();
    return (
        <PageContainer breadcrumbs={[{ label: t('SIDEBAR.SHOPPING_ASSISTANT.TITLE', 'Shopping Assistant') }, { label: t('SIDEBAR.SHOPPING_ASSISTANT.QUESTIONS', 'Shopper questions') }]}>
            <FeatureGate feature="shopperChat">
                <QuestionsBody />
            </FeatureGate>
        </PageContainer>
    );
};

export default AssistantQuestionsPage;
